import React, { memo, useEffect, useMemo, useCallback, useState, useRef } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import cx from 'classnames'

import { makeStyles } from '@material-ui/core/styles'
import { Divider, IconButton, List, ListItem, Tooltip, Typography, Grid } from '@material-ui/core'

import { getWhatsAppUrl } from '~/utils'

import Placeholder from './Placeholder'
import IfoodBanner from './ifoodBanner'

import WhatsappFormDialog from '~/containers/WhatsAppForm'
import StoreNotFound from '~/components/StoreNotFound'
import WhatsIcon from '~/components/WhatsIcon'
import { useLiterals } from '~/components/Literals'

import track, { cliqueRota, cliqueWhats, cliqueLoja } from '~/track'
import { selectStores } from '~/selectors'
import { getExtraInfoByBrand } from './helper'
import actions from '~/actions'
import SegmentTag from '../SegmentTag'
import Distance from './Distance'
import FilteredStoresNotFound from './FilteredStoresNotFound'

const useStyles = makeStyles((theme) => ({
  list: {
    marginTop: -10,
    [theme.breakpoints.up('md')]: {
      marginBottom: 92,
    },
  },
  listRoot: {
    gap: 16,
    flexDirection: 'column',
    padding: '24px 16px',
  },
  text: {
    width: '100%',
    '& .single-line': {
      ...theme.lineLimit(),
    },
    '& .double-line': {
      ...theme.lineLimit(2),
    },
    '& .store-name': {
      fontSize: 14,
      fontWeight: 600,
    },
    '& .store-address': {
      fontSize: 14,
      fontWeight: 400,
    },
    '& .store-info': {
      fontSize: 12,
      color: '#B5B3B3',
    },
  },
  featured: {
    background: '#fffbc8',
  },
  storeHover: {
    background: '#f2f2f2',
  },
}))

function Store({
  selectedProducts,
  classes,
  last,
  onMouseEnter,
  product,
  config,
  store,
  storeHover,
  position,
  openWhatsForm,
  trackClickWhatsapp,
  handleToggle,
}) {
  const { name, distance, coordinates, cnpj, whatsapp, stats, segmentation } = store
  const { getLiteral } = useLiterals()
  const history = useHistory()
  const dispatch = useDispatch()

  const handleClick = useCallback(() => {
    cliqueLoja(config, store, product, position.user)
    dispatch(actions.selectStore(store))
    history.push(`/loja/${window.btoa(cnpj)}`)
    handleToggle && handleToggle()
  }, [cnpj, store.name, handleToggle, dispatch])

  const brandName = config?.brandName
  const isEstablishment = Boolean(config?.showContextViewEstablishment)

  const hideStockMessage = config.multiProduct && !store.featured
  const showSelectedProducts = config.multiProduct && !product
  const extraInfo = useMemo(
    () => store?.locatorExtraInfo && getExtraInfoByBrand(store.locatorExtraInfo, config.cnpj),
    [store, config.cnpj],
  )

  const currentDateInMillis = new Date().getTime()
  const storeFeaturedWithLimitDate = store?.featured?.limitDate && new Date(store?.featured?.limitDate).getTime()
  const isWithinLimit = currentDateInMillis <= storeFeaturedWithLimitDate

  const handleClickRota = (e) => {
    e.stopPropagation()
    cliqueRota(config, store, product, position?.user)
  }

  return (
    <>
      <ListItem
        className={cx({
          [classes.featured]: Boolean(store?.featured) && isWithinLimit,
          [classes.storeHover]: !store?.featured && storeHover,
        })}
        classes={{ root: classes.listRoot }}
        onClick={handleClick}
        button
        onMouseEnter={onMouseEnter}
        onFocus={onMouseEnter}
        data-cy={`store-${name}`}
        data_analytics_category="onde-comprar"
        data_analytics_action="select_partner"
        data_analytics_partner_name={store?.name}
        data_analytics_partner_address={store?.coordinates?.formattedAddress}
        data_analytics_product_id={product?.gtin}
        data_analytics_product_name={product?.name}
        data_analytics_product_size={product?.name}
        data_analytics_product_type={product?.name}
        data_analytics_local={position?.user?.city}
      >
        <Grid container display="flex">
          {segmentation && (
            <Grid container item xs={12}>
              <Grid item xs={9} md={10} style={{ display: 'flex', alignItems: 'center' }}>
                <SegmentTag segment={store?.segmentation} />
              </Grid>
              {/* distance */}
              <Grid
                item
                xs={3}
                md={2}
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 14,
                }}
              >
                <Distance distance={distance} handleClickRota={handleClickRota} getLiteral={getLiteral} />
              </Grid>
            </Grid>
          )}

          <Grid container item xs={12} className={classes.text}>
            <Grid item xs={!segmentation ? 9 : 12}>
              <Typography variant="subtitle1" color="inherit" className="single-line store-name">
                {name}
              </Typography>
            </Grid>

            {!segmentation && (
              <Grid
                item
                xs={3}
                style={{
                  display: 'flex',
                  justifyContent: 'end',
                  gap: 4,
                  fontSize: 14,
                  textAlign: 'right',
                }}
              >
                <Distance distance={distance} handleClickRota={handleClickRota} getLiteral={getLiteral} />
              </Grid>
            )}

            <Grid item xs={whatsapp ? 10 : 12}>
              <Typography variant="body1" color="inherit" className="double-line store-address">
                {coordinates.formattedAddress}
              </Typography>

              <Grid item xs={9} md={10} style={{ marginTop: 10 }}>
                {extraInfo?.length ? (
                  <Typography variant="body2" className="store-info">
                    {extraInfo}
                  </Typography>
                ) : !hideStockMessage && (isEstablishment || product.gtin !== 'all') ? (
                  <Typography variant="body2" className="store-info">
                    {store.stockMessage}
                  </Typography>
                ) : null}
              </Grid>
            </Grid>

            {!!whatsapp && (
              <Grid item xs={2} style={{ display: 'flex', justifyContent: 'end' }}>
                <Tooltip title={getLiteral('2188')}>
                  {config.whatsappForm ? (
                    <IconButton
                      onClick={(e) => {
                        openWhatsForm()
                        e.stopPropagation()
                      }}
                    >
                      <WhatsIcon color="inherit" fullColor />
                    </IconButton>
                  ) : (
                    <IconButton
                      onClick={(e) => {
                        trackClickWhatsapp()
                        e.stopPropagation()
                      }}
                      component="a"
                      href={getWhatsAppUrl(
                        whatsapp,
                        name,
                        product?.name ||
                          Object.values(store.stats)
                            .map((i) => i.name)
                            .join(),
                        brandName,
                        isEstablishment,
                        getLiteral,
                        config?.textToWhatsapp,
                      )}
                      target="_blank"
                    >
                      <WhatsIcon color="inherit" fullColor />
                    </IconButton>
                  )}
                </Tooltip>
              </Grid>
            )}

            <Grid item xs={9} md={10}>
              {showSelectedProducts && (
                <Typography variant="body2" className="store-info">
                  {getLiteral('2187', [Object.keys(stats).length, selectedProducts.length])}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      </ListItem>
      {!last && <Divider />}
    </>
  )
}

const MemoizedStore = memo(Store)

const StoreList = ({ currentProduct, storeHover, setHover, position, handleToggle }) => {
  const whatsappTotalWasSent = useRef(false)
  const config = useSelector((store) => store.config)
  const { stores, loading, selectedProducts } = useSelector(selectStores, shallowEqual)
  const { showContextViewStore } = config
  const classes = useStyles({ storesNameViewFormat: config.storesNameViewFormat })
  const [whatsappForm, setWhatsAppForm] = useState(false)
  const dispatch = useDispatch()

  useEffect(() => {
    if (!whatsappTotalWasSent.current && config && stores) {
      track('whatsappTotal', {
        name: stores.filter(({ whatsapp }) => {
          return whatsapp && whatsapp.trim()
        }).length,
        category: 'lojas',
      })
      whatsappTotalWasSent.current = true
    }
  }, [stores, config])

  const trackClickWhatsapp = useCallback(() => {
    currentProduct && Object.values(currentProduct).some((v) => Boolean(v)) && cliqueWhats(currentProduct)
  }, [currentProduct])

  const openWhatsappForm = (store) => {
    dispatch(actions.updateApp({ currentStore: store }))
    setWhatsAppForm(true)
  }

  if (loading || !currentProduct) {
    return <Placeholder />
  }

  // lojas não encontradas
  if (!stores) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          justifyContent: 'center',
          alignItems: 'center',
          paddingTop: 20,
        }}
      >
        <StoreNotFound />
      </div>
    )
  }

  return (
    <>
      <WhatsappFormDialog open={whatsappForm} setOpen={setWhatsAppForm} track={trackClickWhatsapp} />
      <List className={classes.list}>
        {currentProduct?.gtin === 'all' && showContextViewStore && stores.length > 10 ? (
          stores.slice(0, 10).map((store, index) => (
            <Grid key={store.cnpj}>
              <MemoizedStore
                product={currentProduct}
                config={config}
                selectedProducts={selectedProducts}
                key={store.cnpj}
                store={store}
                last={index === store.length - 1}
                storeHover={storeHover === index}
                classes={classes}
                position={position}
                onMouseEnter={() => setHover(index)}
                onMouseLeave={() => setHover(null)}
                openWhatsForm={() => openWhatsappForm(store)}
                trackClickWhatsapp={trackClickWhatsapp}
                handleToggle={handleToggle}
              />
              {config?.iFoodApi && index === 1 && (
                <>
                  <IfoodBanner url={config?.iFoodApi} />
                  <Divider />
                </>
              )}
            </Grid>
          ))
        ) : (
          <>
            {stores.map((store, index) => (
              <Grid key={store.cnpj}>
                <MemoizedStore
                  product={currentProduct}
                  config={config}
                  selectedProducts={selectedProducts}
                  key={store.cnpj}
                  store={store}
                  last={index === store.length - 1}
                  storeHover={storeHover === index}
                  classes={classes}
                  position={position}
                  onMouseEnter={() => setHover(index)}
                  onMouseLeave={() => setHover(null)}
                  openWhatsForm={() => openWhatsappForm(store)}
                  trackClickWhatsapp={trackClickWhatsapp}
                  handleToggle={handleToggle}
                />
                {config?.iFoodApi && (stores.length <= 1 || index === 1) && (
                  <>
                    <IfoodBanner url={config?.iFoodApi} />
                    <Divider />
                  </>
                )}
              </Grid>
            ))}
          </>
        )}
      </List>

      {!stores.length && <FilteredStoresNotFound />}
    </>
  )
}

export default memo(StoreList)
